<template>
  <div class="product-list-container">
    <div class="sidebar">
      <h3>{{ $t('product.categories.title') }}</h3>
      <ul>
        <li 
          v-for="(category, index) in categories" 
          :key="index" 
          :class="{ active: currentCategory === category.value }"
          @click="loadCategory(category.value)">
          {{ $t(getCategoryTranslationKey(category.value)) }}
        </li>
      </ul>
    </div>
    
    <div class="main-content">
      <div class="search-bar">
        <input 
          type="text" 
          v-model="searchQuery" 
          :placeholder="$t('product.search')" 
          @input="onSearchInput"
        />
      </div>
      
      <div class="filters">
        <template v-for="(options, field) in filterOptions" :key="field">
          <!-- 隐藏 Product Status 筛选器 -->
          <div class="filter-group" v-if="field !== 'ProductStatus'">
            <h4>{{ field }}</h4>
            <div class="filter-options">
              <select 
                class="form-select" 
                v-model="selectedFilters[field]" 
                multiple
                @change="onFilterChange"
                size="4"
              >
                <option v-for="option in options" :key="option" :value="option">
                  {{ option }}
                </option>
              </select>
            </div>
          </div>
        </template>
      </div>
      
      <div class="products-table">
        <table v-if="total > 0">
          <thead>
            <tr>
              <th v-for="header in tableHeaders" :key="header" @click="sortBy(header)">
                {{ getDisplayName(header) }}
                <span v-if="sortKey === header" class="sort-indicator">
                  {{ sortOrder === 'asc' ? '▲' : '▼' }}
                </span>
              </th>
              <th>{{ $t('product.table.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, index) in paginatedProducts" :key="index">
              <td v-for="header in tableHeaders" :key="header">
                <template v-if="header === 'Title'">
                  <a :href="'/product/' + (product.id || '')" @click.prevent="viewDetail(product)" class="product-link">
                    {{ product[header] }}
                  </a>
                </template>
                <template v-else-if="header === 'FileUrl' && product[header]">
                  <a :href="getFileUrl(product[header])" target="_blank">{{ $t('product.table.view_pdf') }}</a>
                </template>
                <template v-else>
                  {{ product[header] }}
                </template>
              </td>
              <td>
                <button class="purchase-btn" @click="openPurchaseForm(product)">
                  {{ $t('product.purchase_button') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="no-products">
          {{ $t('product.no_results') }}
        </div>
        
        <!-- 加载状态 -->
        <div v-if="loading" class="loading">
          {{ $t('message.loading') }}
        </div>
        
        <!-- 分页控件 -->
        <div class="pagination" v-if="total > 0 && !loading">
          <button 
            class="pagination-btn" 
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          >
            {{ $t('pagination.previous') }}
          </button>
          
          <span class="page-info">
            {{ $t('pagination.page_info', { current: currentPage, total: totalPages, count: total }) }}
          </span>
          
          <button 
            class="pagination-btn" 
            :disabled="currentPage === totalPages"
            @click="changePage(currentPage + 1)"
          >
            {{ $t('pagination.next') }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- 购买表单 -->
    <PurchaseForm 
      v-if="showPurchaseForm" 
      :product="selectedProduct"
      @close="closePurchaseForm"
    />
  </div>
</template>

<script>
import PurchaseForm from './PurchaseForm.vue';
import api from '../api';

export default {
  name: 'ProductList',
  components: {
    PurchaseForm
  },
  data() {
    return {
      showPurchaseForm: false,
      selectedProduct: null,
      categories: [
        { label: '消费级Mosfet', value: 'xfjMosfet' },
        { label: '工业级Mosfet', value: 'gyjMosfet' },
        { label: '车规级Mosfet', value: 'cgjMosfet' },
        { label: 'Power Module', value: 'powermodule' },
        { label: 'OVP', value: 'ovp' },
        { label: 'LDO', value: 'ldo' },
        { label: 'PMIC', value: 'pmic' }
      ],
      currentCategory: 'xfjMosfet',
      products: [],          // 当前页数据（来自后端）
      total: 0,              // 后端返回的总条数
      searchQuery: '',
      filterOptions: {},
      selectedFilters: {
        Package: [],
        ProductStatus: [],
        MOSFETType: [],
        Technology: [],
        IDA: [],
        VDSMin: []
      },
      tableHeaders: ['Title', 'Package', 'MOSFETType', 'Technology', 'IDA', 'VDSMin', 'FileUrl'],
      sortKey: 'Title',
      sortOrder: 'asc',
      currentPage: 1,
      pageSize: 20,
      loading: false,
      searchTimer: null      // 搜索防抖定时器
    }
  },
  computed: {
    // 计算总页数（基于后端返回的总数）
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    // 当前页数据（后端已分页，直接使用）
    paginatedProducts() {
      return this.products;
    }
  },
  mounted() {
    this.fetchProducts();
  },
  methods: {
    // 构建查询参数
    buildQuery() {
      const params = new URLSearchParams();
      params.set('page', this.currentPage);
      params.set('pageSize', this.pageSize);
      if (this.searchQuery.trim()) params.set('search', this.searchQuery.trim());
      params.set('sortKey', this.sortKey);
      params.set('sortOrder', this.sortOrder);
      Object.entries(this.selectedFilters).forEach(([field, values]) => {
        values.forEach(v => params.append(field, v));
      });
      return params.toString();
    },

    // 向后端请求（分页/搜索/筛选/排序统一入口）
    async fetchProducts() {
      this.loading = true;
      try {
        // 走 axios 实例（baseURL = https://api.765.info），避免原生 fetch 把相对路径
        // 拼到当前站点域名（如 https://765.info/api，导致 404）
        const response = await api.get(`/api/products/${this.currentCategory}?${this.buildQuery()}`);
        const result = response.data;
        if (!result.success) throw new Error(result.message || '读取产品数据失败');
        this.products = Array.isArray(result.data) ? result.data : [];
        this.total = result.total || 0;
        // 只在首次进入该分类（或筛选选项为空）时更新下拉项
        if (result.filterOptions && Object.keys(this.filterOptions).length === 0) {
          this.filterOptions = result.filterOptions;
        }
      } catch (error) {
        console.error(`${this.currentCategory} 加载数据失败:`, error);
        this.products = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    // 切换分类：重置分页/筛选并从后端重新拉取
    loadCategory(category) {
      this.currentCategory = category;
      this.currentPage = 1;
      this.searchQuery = '';
      this.selectedFilters = {
        Package: [], ProductStatus: [], MOSFETType: [], Technology: [], IDA: [], VDSMin: []
      };
      this.filterOptions = {};
      this.fetchProducts();
    },

    // 搜索（带防抖）
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.fetchProducts();
      }, 300);
    },

    // 筛选变化
    onFilterChange() {
      this.currentPage = 1;
      this.fetchProducts();
    },

    // 排序
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortOrder = 'asc';
      }
      this.currentPage = 1;
      this.fetchProducts();
    },
    getDisplayName(header) {
      const translationKeyMap = {
        'Title': 'model',
        'Package': 'package',
        'ProductStatus': 'product_status',
        'MOSFETType': 'mosfet_type',
        'Technology': 'technology',
        'IDA': 'current',
        'VDSMin': 'voltage',
        'FileUrl': 'datasheet'
      };
      const translationKey = translationKeyMap[header];
      return translationKey ? this.$t(`product.table.${translationKey}`) : header;
    },
    getFileUrl(url) {
      if (!url) return '';
      // 已经是完整 URL 则原样返回
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      // 数据手册托管在 api.765.info/pdf/ 下，拼接成完整链接
      const base = process.env.VUE_APP_PDF_BASE || 'https://api.765.info/pdf';
      return `${base}/${url.replace(/^\/+/, '')}`;
    },
    getCategoryTranslationKey(categoryValue) {
      const categoryMap = {
        'xfjMosfet': 'product.categories.consumer_mosfet',
        'gyjMosfet': 'product.categories.industrial_mosfet',
        'cgjMosfet': 'product.categories.automotive_mosfet',
        'powermodule': 'product.categories.power_module',
        'ovp': 'product.categories.ovp',
        'ldo': 'product.categories.ldo',
        'pmic': 'product.categories.pmic'
      };
      return categoryMap[categoryValue] || categoryValue;
    },
    
    // 分页方法 - 切换页面
    changePage(page) {
      if (page < 1) page = 1;
      else if (page > this.totalPages) page = this.totalPages;
      this.currentPage = page;
      this.fetchProducts();
      // 滚动到页面顶部
      window.scrollTo({
        top: this.$el.offsetTop,
        behavior: 'smooth'
      });
    },
    
    // 打开购买表单
    openPurchaseForm(product) {
      this.selectedProduct = product;
      this.showPurchaseForm = true;
    },
    viewDetail(product) {
      this.$emit('view-detail', product, this.currentCategory);
    },
    
    // 关闭购买表单
    closePurchaseForm() {
      this.showPurchaseForm = false;
      this.selectedProduct = null;
    }
  }
}
</script>

<style scoped>
.product-list-container {
  display: flex;
  margin: 20px;
  gap: 20px;
  text-align: left;
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.sidebar {
  width: 200px;
  padding: 20px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  height: fit-content;
}

.sidebar h3 {
  margin-top: 0;
  color: #2c3e50;
  font-size: 1.2em;
  padding-bottom: 10px;
  border-bottom: 2px solid #42b983;
}

.sidebar ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.sidebar li {
  padding: 12px 15px;
  cursor: pointer;
  border-radius: 4px;
  margin: 5px 0;
  transition: all 0.3s ease;
}

.sidebar li:hover {
  background-color: #f8f9fa;
  color: #42b983;
}

.sidebar li.active {
  background-color: #42b983;
  color: white;
  font-weight: 500;
}

.main-content {
  flex: 1;
  min-width: 0;
}

.search-bar {
  margin-bottom: 20px;
}

.search-bar input {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 1em;
  transition: border-color 0.3s ease;
}

.search-bar input:focus {
  outline: none;
  border-color: #42b983;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
  padding: 20px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.filter-group {
  min-width: 180px;
  flex: 1;
}

.filter-group h4 {
  margin: 0 0 10px 0;
  color: #2c3e50;
  font-size: 1em;
  font-weight: 500;
}

.filter-options {
  margin-top: 8px;
}

.filter-options select {
  width: 100%;
  padding: 8px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background-color: #fff;
  font-size: 0.95em;
}

.filter-options select:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.1);
}

.filter-options select option {
  padding: 8px;
}

.filter-options select option:checked {
  background-color: #42b983;
  color: white;
}

.products-table {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95em;
}

th {
  background-color: #f8f9fa;
  color: #2c3e50;
  font-weight: 600;
  padding: 15px;
  text-align: left;
  border-bottom: 2px solid #e9ecef;
  cursor: pointer;
  transition: background-color 0.3s ease;
  white-space: nowrap;
}

th:hover {
  background-color: #e9ecef;
}

.sort-indicator {
  margin-left: 5px;
  color: #42b983;
}

td {
  padding: 12px 15px;
  border-bottom: 1px solid #e9ecef;
}

tr:hover {
  background-color: #f8f9fa;
}

td a {
  color: #42b983;
  text-decoration: none;
  font-weight: 500;
}

td a:hover {
  text-decoration: underline;
}

.no-products {
  padding: 30px;
  text-align: center;
  color: #6c757d;
  font-size: 1.1em;
}

@media (max-width: 1024px) {
  .product-list-container {
    flex-direction: column;
  }
  
  .sidebar {
    width: 100%;
  }
  
  .filter-group {
    min-width: 150px;
  }
}

@media (max-width: 768px) {
  .products-table {
    overflow-x: auto;
  }
  
  table {
    font-size: 0.9em;
  }
  
  th, td {
    padding: 10px;
  }
}

/* 加载状态样式 */
.loading {
  padding: 40px;
  text-align: center;
  font-size: 1.1em;
  color: #6c757d;
}

/* 分页控件样式 */
.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  padding: 15px 0;
}

.pagination-btn {
  padding: 10px 20px;
  border: 1px solid #e9ecef;
  background-color: #ffffff;
  color: #2c3e50;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.3s ease;
  font-size: 0.95em;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.pagination-btn:hover:not(:disabled) {
  background-color: #42b983;
  color: white;
  border-color: #42b983;
}

.pagination-btn:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
  color: #adb5bd;
  border-color: #e9ecef;
}

.page-info {
  font-size: 0.95em;
  color: #6c757d;
  padding: 0 10px;
}

.product-link {
  color: #42b983;
  text-decoration: none;
  font-weight: bold;
  transition: color 0.3s;
}

.product-link:hover {
  color: #3aa876;
  text-decoration: underline;
}

@media (max-width: 480px) {
  .pagination {
    flex-direction: column;
    gap: 10px;
  }
  
  .pagination-btn {
    width: 100%;
  }
}
</style>